import { defineComponent as _defineComponent } from 'vue'

const __default__ = { name: 'DataTable' }

import { computed, useSlots } from 'vue'
import { BPagination } from 'bootstrap-vue'
import draggable from 'vuedraggable'
import { nextSort } from '@/util/sortCycle'

export interface DataTableColumn {
  key: string
  label: string
  width?: string
  align?: 'left' | 'right' | 'center'
  sortable?: boolean
  // sort key sent to the server when it differs from the column key
  sortKey?: string
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  columns: { type: Array as () => DataTableColumn[], required: true },
  rows: { type: Array as () => any[], default: () => [] },
  rowKey: { type: String, default: 'id' },
  rowClass: { type: Function as unknown as () => ((row: any) => string) | null, default: null },
  loading: { type: Boolean, default: false },
  // Whole row opens the record (emits row-click); the actions cell stops propagation.
  clickable: { type: Boolean, default: false },
  page: { type: Number, default: 1 },
  perPage: { type: Number, default: 10 },
  total: { type: Number, default: 0 },
  sortValue: { type: Object as () => { key: string; dir: 'asc' | 'desc' } | null, default: null },
  // The page's default order. When set, a third click on a sorted column returns to
  // it (ascending, descending, default); without it a column toggles asc and desc.
  defaultSort: { type: Object as () => { key: string; dir: 'asc' | 'desc' } | null, default: null },
  reorderable: { type: Boolean, default: false },
},
  emits: ["update:page", "update:sort", "row-click", "reorder"],
  setup(__props, { emit }: { emit: ({
  (e: 'update:page', page: number): void
  (e: 'update:sort', value: { key: string; dir: 'asc' | 'desc' }): void
  (e: 'row-click', row: any): void
  (e: 'reorder', rows: any[]): void
}), expose: any, slots: any, attrs: any }) {

const props = __props

// House table for list pages, modeled on Stripe's dashboard tables: quiet uppercase
// headers, hairline rows, sortable headers that share the FilterBar sort value,
// a row actions column, and stacked label/value rows on narrow screens.
//
// Pairs with FilterBar for filters, count, per page and CSV. Paging is server
// side: the page passes page/perPage/total and re-fetches on update:page.
//
// `reorderable` adds a drag handle column and emits `reorder` with the visible
// rows in their new order; the page splices that back into its full list and
// persists it. Pages enable it only while the list is in its stored order (no
// sort, no filters) so a drag can never disagree with what the user sees.
//
// <data-table :columns="cols" :rows="rows" row-key="id" :page="page" :per-page="perPage"
//   :total="total" :sort-value="sort" :default-sort="{ key: 'name', dir: 'asc' }"
//   @update:sort="..." @update:page="...">
//   <template #cell(name)="{ row }">...</template>
//   <template #actions="{ row }"> <el-dropdown> ... </el-dropdown> </template>
// </data-table>




const slots = useSlots()
const hasActions = computed(() => !!slots.actions)

// First load (no rows yet) shows shimmer rows from the global .skel primitive;
// a refetch over existing rows only dims them so the page does not jump.
const showSkeleton = computed(() => props.loading && props.rows.length === 0)
// The empty state stands alone: no column headers over a table with nothing in it.
const showEmpty = computed(() => !props.loading && props.rows.length === 0)
const skeletonRows = computed(() => Math.min(Math.max(props.perPage, 3), 8))
const widths = ['70%', '55%', '80%', '45%', '65%']
function skeletonWidth(col: DataTableColumn, n: number) {
  return widths[(n + col.key.length) % widths.length]
}

// vuedraggable in `value` mode emits `input` with a fresh array and never
// mutates the rows prop.
const bodyTag = computed(() => (props.reorderable ? draggable : 'tbody'))
const bodyProps = computed(() =>
  props.reorderable
    ? { tag: 'tbody', value: props.rows, handle: '.data-table__handle', fallbackTolerance: 5, animation: 150 }
    : {},
)
function onReorder(rows: any[]) {
  if (props.reorderable && Array.isArray(rows)) emit('reorder', rows)
}
// Footer: pager bottom left, "1 to 10 of 35 results" right. Per page lives in the
// filter bar's right cluster, and the filter bar omits its count on pages that
// use this table so the number appears once.
// Position only. The total lives in the FilterBar's result count next to the
// filters, so the footer never repeats it.
const rangeText = computed(() => {
  const start = (props.page - 1) * props.perPage + 1
  const end = Math.min(props.page * props.perPage, props.total)
  if (props.total <= props.perPage) return ''
  return `${start} to ${end}`
})

function rowId(row: any) {
  return row[props.rowKey]
}

function sortKeyOf(col: DataTableColumn) {
  return col.sortKey || col.key
}

function ariaSort(col: DataTableColumn) {
  if (!col.sortable || !props.sortValue || props.sortValue.key !== sortKeyOf(col)) return undefined
  return props.sortValue.dir === 'asc' ? 'ascending' : 'descending'
}

// Ascending, descending, then the page default when one is set (see nextSort).
function onHeaderClick(col: DataTableColumn) {
  emit('update:sort', nextSort(props.sortValue, sortKeyOf(col), props.defaultSort))
}

function onPage(page: number) {
  if (page !== props.page) emit('update:page', page)
}

return { __sfc: true,props, emit, slots, hasActions, showSkeleton, showEmpty, skeletonRows, widths, skeletonWidth, bodyTag, bodyProps, onReorder, rangeText, rowId, sortKeyOf, ariaSort, onHeaderClick, onPage, BPagination }
}

})